// ===================================================================
// KAIXO - Invitar a seguir un canal
// ===================================================================
// Una sola lista con TODOS los usuarios invitables (que no siguen ya
// al canal). Los que dieron ❤️ a algún latido aparecen arriba con un
// tag destacado. Resto ordenado por nombre. El dueño marca checkboxes
// y envía; cada invitado recibe un mensaje privado con link al canal.
// ===================================================================

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { v2GetInviteCandidates, v2InviteToFollow, getUserGroups } from '../../services/api';
import { Icons } from '../Icons/Icons';
import './InviteFollowersModal.css';

function InviteFollowersModal({ channelId, channelName, currentUserId, onClose }) {
    const [loading, setLoading] = useState(true);
    const [candidates, setCandidates] = useState([]);
    const [groups, setGroups] = useState([]);                 // grupos del usuario
    const [selected, setSelected] = useState(new Set());      // userIds individuales
    const [selectedGroups, setSelectedGroups] = useState(new Set()); // groupIds
    const [query, setQuery] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null); // { sent, total }
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Carga paralela: candidatos + grupos del owner. Si falla
                // grupos, seguimos con la lista de usuarios.
                let uid = currentUserId;
                if (!uid) {
                    try { uid = JSON.parse(localStorage.getItem('kaixo_user') || '{}')._id || null; } catch {}
                }
                const [{ data: invData }, groupsResp] = await Promise.all([
                    v2GetInviteCandidates(channelId),
                    uid ? getUserGroups(uid).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
                ]);
                if (cancelled) return;

                if (Array.isArray(invData.candidates)) {
                    setCandidates(invData.candidates);
                } else {
                    const likers = (invData.likers || []).map(u => ({ ...u, hasLiked: true }));
                    const others = (invData.others || []).map(u => ({ ...u, hasLiked: false }));
                    setCandidates([...likers, ...others]);
                }

                const groupsList = Array.isArray(groupsResp?.data)
                    ? groupsResp.data
                    : (groupsResp?.data?.groups || []);
                setGroups(groupsList);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.error || 'No se pudo cargar la lista');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [channelId, currentUserId]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const toggleGroup = (gid) => {
        const next = new Set(selectedGroups);
        if (next.has(gid)) next.delete(gid); else next.add(gid);
        setSelectedGroups(next);
    };

    // Cuántos destinatarios ÚNICOS reales tendríamos al enviar (suma users
    // individuales + miembros únicos de cada grupo seleccionado, sin contar
    // al propio dueño del canal).
    const computeRecipientIds = () => {
        const set = new Set();
        for (const id of selected) set.add(String(id));
        for (const gid of selectedGroups) {
            const g = groups.find(x => String(x._id) === String(gid));
            if (!g) continue;
            const memberArr = Array.isArray(g.members) ? g.members : [];
            for (const m of memberArr) {
                const mid = typeof m === 'object' ? (m._id || m.id) : m;
                if (!mid) continue;
                const sm = String(mid);
                if (currentUserId && sm === String(currentUserId)) continue;
                set.add(sm);
            }
        }
        return Array.from(set);
    };

    const totalRecipients = computeRecipientIds().length;

    const handleSend = async () => {
        const recipients = computeRecipientIds();
        if (recipients.length === 0) return;
        setSending(true);
        setError('');
        try {
            const { data } = await v2InviteToFollow(channelId, recipients);
            setResult({ sent: data.sent || 0, total: data.total || recipients.length });
            setSelected(new Set());
            setSelectedGroups(new Set());
        } catch (e) {
            setError(e?.response?.data?.error || 'Error al enviar invitaciones');
        } finally {
            setSending(false);
        }
    };

    // Filtro local sobre la lista unificada (sin slicing — mostramos todos)
    const q = query.trim().toLowerCase();
    const visible = q
        ? candidates.filter(u => (u.name || '').toLowerCase().includes(q))
        : candidates;
    const likerCount = candidates.filter(u => u.hasLiked).length;
    const visibleGroups = q
        ? groups.filter(g => (g.name || '').toLowerCase().includes(q))
        : groups;

    const content = (
        <div className="inv-overlay" onClick={onClose}>
            <div className="inv-modal" onClick={e => e.stopPropagation()}>
                <div className="inv-header">
                    <h3>Invita a seguir «{channelName}»</h3>
                    <button className="inv-close" onClick={onClose} aria-label="Cerrar">×</button>
                </div>

                {loading && <div className="inv-empty">Cargando…</div>}

                {result && (
                    <div className="inv-success">
                        ✅ Invitaciones enviadas a {result.sent} de {result.total} personas.
                        <button className="inv-success-close" onClick={onClose}>Cerrar</button>
                    </div>
                )}

                {!loading && !result && (
                    <div className="inv-section">
                        <div className="inv-search">
                            <input
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                                placeholder="Buscar por nombre..."
                            />
                        </div>

                        {visibleGroups.length > 0 && (
                            <>
                                <div className="inv-section-title">
                                    <span style={{ fontSize: 14 }}>👥</span>
                                    <span>Grupos ({visibleGroups.length})</span>
                                </div>
                                <div className="inv-list">
                                    {visibleGroups.map(g => (
                                        <GroupRow
                                            key={g._id}
                                            group={g}
                                            checked={selectedGroups.has(g._id)}
                                            onToggle={() => toggleGroup(g._id)}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        <div className="inv-section-title">
                            <Icons.Users size={14} />
                            <span>
                                Contactos ({candidates.length})
                                {likerCount > 0 && <> · <Icons.Heart size={12} color="#e53935" /> {likerCount} dieron me gusta</>}
                            </span>
                        </div>
                        <div className="inv-list">
                            {visible.length === 0 && (
                                <div className="inv-empty">{q ? 'Sin resultados' : 'Sin contactos disponibles'}</div>
                            )}
                            {visible.map(u => (
                                <UserRow
                                    key={u._id}
                                    user={u}
                                    subtitle={u.hasLiked
                                        ? `❤️ ${u.likeCount} me gusta a tus latidos`
                                        : null}
                                    checked={selected.has(u._id)}
                                    onToggle={() => toggle(u._id)}
                                    highlight={u.hasLiked}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {error && <div className="inv-error">{error}</div>}

                {!result && (
                    <div className="inv-footer">
                        <button className="inv-cancel" onClick={onClose}>Cancelar</button>
                        <button
                            className="inv-send"
                            disabled={sending || totalRecipients === 0 || totalRecipients > 20}
                            onClick={handleSend}
                        >
                            {sending ? 'Enviando…' : `Invitar a ${totalRecipients}`}
                        </button>
                    </div>
                )}
                {totalRecipients > 20 && (
                    <div className="inv-error">Máximo 20 invitaciones a la vez · seleccionados {totalRecipients}</div>
                )}
            </div>
        </div>
    );

    return ReactDOM.createPortal(content, document.body);
}

function UserRow({ user, subtitle, checked, onToggle, highlight }) {
    const classes = ['inv-row'];
    if (checked) classes.push('checked');
    if (highlight) classes.push('highlight');
    return (
        <label className={classes.join(' ')}>
            <div className="inv-avatar">
                {user.avatar
                    ? <img src={user.avatar} alt="" />
                    : (user.name?.[0] || '?').toUpperCase()}
            </div>
            <div className="inv-info">
                <div className="inv-name">{user.name || 'Usuario'}</div>
                {subtitle && <div className="inv-subtitle">{subtitle}</div>}
            </div>
            <input
                type="checkbox"
                checked={checked}
                onChange={onToggle}
                className="inv-check"
            />
        </label>
    );
}

function GroupRow({ group, checked, onToggle }) {
    const classes = ['inv-row'];
    if (checked) classes.push('checked');
    const memberCount = Array.isArray(group.members) ? group.members.length : 0;
    return (
        <label className={classes.join(' ')}>
            <div className="inv-avatar inv-avatar-group">
                {group.avatar
                    ? <img src={group.avatar} alt="" />
                    : <span>👥</span>}
            </div>
            <div className="inv-info">
                <div className="inv-name">{group.name || 'Grupo'}</div>
                <div className="inv-subtitle">{memberCount} miembros</div>
            </div>
            <input
                type="checkbox"
                checked={checked}
                onChange={onToggle}
                className="inv-check"
            />
        </label>
    );
}

export default InviteFollowersModal;
